import {
  WizardHeader,
  WizardLink,
} from '@monorepo/shared/componentsV2/fullPageWizard/WizardHeader';
import { useLinkOnSameOrg } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameOrg';
import { useIsSuperAdmin } from '@monorepo/shared/hooks/permissions/useIsSuperAdmin';
import { NonEmptyArray } from 'mapistry-shared';
import { useFeatureFlags } from 'mapistry-shared/api';
import React, { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Mode } from './types';

export enum WizardStep {
  DETAILS = 'Basic Info',
  FIELDS = 'Entry Fields',
  AI = 'AI Setup',
  FORMULAS = 'Formula Fields',
  SITES = 'Sites',
  ANALYTICS_SYNC = 'Analytics',
}

// the last bit of the URL each one of these steps is found on
export const WizardStepUrlPart: Record<WizardStep, string> = {
  [WizardStep.DETAILS]: '',
  [WizardStep.FIELDS]: 'fields',
  [WizardStep.AI]: 'ai',
  [WizardStep.FORMULAS]: 'formulas',
  [WizardStep.SITES]: 'sites',
  [WizardStep.ANALYTICS_SYNC]: 'analytics',
};

/** Tabs in display order, which also sets step completion and where saving goes next. */
function useWizardSteps(): NonEmptyArray<WizardStep> {
  const isSuperAdmin = useIsSuperAdmin();
  const { hasOrgPaidForEdpAiUploader } = useFeatureFlags();
  const { mode = Mode.new } = useParams();
  const isEditMode = mode === Mode.edit;

  return useMemo<NonEmptyArray<WizardStep>>(
    () => [
      WizardStep.DETAILS,
      WizardStep.FIELDS,
      ...(hasOrgPaidForEdpAiUploader ? [WizardStep.AI] : []),
      WizardStep.FORMULAS,
      WizardStep.SITES,
      ...(isSuperAdmin && isEditMode ? [WizardStep.ANALYTICS_SYNC] : []),
    ],
    [hasOrgPaidForEdpAiUploader, isSuperAdmin, isEditMode],
  );
}

/** Returns undefined on the last step. */
export function useNextWizardStep(
  currentStep: WizardStep,
): WizardStep | undefined {
  const steps = useWizardSteps();
  return steps[steps.indexOf(currentStep) + 1];
}

interface EditLogHeaderProps {
  canSubmit?: boolean;
  isSubmitting?: boolean;
  onSubmit?: () => unknown;
  wizardStep: WizardStep;
}

export function EditLogHeader({
  canSubmit,
  isSubmitting = false,
  onSubmit,
  wizardStep,
}: EditLogHeaderProps) {
  const steps = useWizardSteps();

  const { mode = Mode.new, logId } = useParams();
  const isEditMode = mode === Mode.edit;
  const logExists = !!logId;

  const navigate = useNavigate();

  const baseLink = useLinkOnSameOrg('/data-setup/logs');
  const handleClose = useCallback(() => {
    navigate(baseLink);
  }, [navigate, baseLink]);

  const breadcrumbsLinks = useMemo<NonEmptyArray<WizardLink>>(() => {
    function getLinkTo(stepToGoTo: WizardStep): string {
      const urlPart = WizardStepUrlPart[stepToGoTo];
      return `${baseLink}${logExists ? `/${logId}` : ''}/${
        isEditMode ? 'edit' : 'new'
      }${urlPart ? `/${urlPart}` : ''}`;
    }

    const currentStepIndex = steps.indexOf(wizardStep);
    function isStepCompleted(step: WizardStep): boolean {
      return steps.indexOf(step) < currentStepIndex;
    }

    const fieldsAreNotReady =
      !isEditMode &&
      (wizardStep === WizardStep.DETAILS ||
        (wizardStep === WizardStep.FIELDS && !canSubmit));
    const linksByStep: Record<WizardStep, WizardLink> = {
      [WizardStep.DETAILS]: {
        completed: logExists,
        label: WizardStep.DETAILS,
        to: getLinkTo(WizardStep.DETAILS),
      },
      [WizardStep.FIELDS]: {
        completed: isStepCompleted(WizardStep.FIELDS),
        disabled: !logExists,
        label: WizardStep.FIELDS,
        to: getLinkTo(WizardStep.FIELDS),
      },
      [WizardStep.AI]: {
        completed: isStepCompleted(WizardStep.AI),
        disabled: !logExists || fieldsAreNotReady,
        label: WizardStep.AI,
        to: getLinkTo(WizardStep.AI),
      },
      [WizardStep.FORMULAS]: {
        completed: isStepCompleted(WizardStep.FORMULAS),
        disabled: !logExists || fieldsAreNotReady,
        label: WizardStep.FORMULAS,
        to: getLinkTo(WizardStep.FORMULAS),
      },
      [WizardStep.SITES]: {
        completed: isStepCompleted(WizardStep.SITES),
        disabled: !logExists || fieldsAreNotReady,
        label: WizardStep.SITES,
        to: getLinkTo(WizardStep.SITES),
      },
      [WizardStep.ANALYTICS_SYNC]: {
        disabled: false,
        label: WizardStep.ANALYTICS_SYNC,
        to: getLinkTo(WizardStep.ANALYTICS_SYNC),
      },
    };
    // Splitting off the first step keeps the NonEmptyArray type, which map() would drop.
    const [firstStep, ...otherSteps] = steps;
    return [
      linksByStep[firstStep],
      ...otherSteps.map((step) => linksByStep[step]),
    ];
  }, [isEditMode, wizardStep, canSubmit, logExists, steps, baseLink, logId]);

  return (
    <WizardHeader
      canSubmit={canSubmit}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      links={breadcrumbsLinks}
      onClose={handleClose}
      onSubmit={onSubmit}
      title={isEditMode ? 'Edit Log' : 'New Log'}
      wizardStep={wizardStep}
    />
  );
}
